import { useEffect, useMemo, useState } from "react";
import { X, Save, FileCheck2, Send } from "lucide-react";
import { axiosInstance } from "../../lib/axios";
import { useInvoiceStore, type Invoice } from "../../store/useInvoiceStore";
import { useClientStore } from "../../store/useClientStore";
import { useProjectStore } from "../../store/useProjectStore";
import { useFinanceStore } from "../../store/useFinanceStore";
import toast from "react-hot-toast";

const PAYMENT_METHODS = ["", "Upwork Escrow", "PayPal", "Payoneer", "Bank Transfer", "Stripe", "Wise", "Cash", "Other"];

export default function InvoiceFormDrawer({
  editing,
  onClose,
  onSaved,
}: {
  editing?: Invoice | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { types, createInvoice, updateInvoice, sendReminder } = useInvoiceStore();
  const { clients, fetchClients } = useClientStore();
  const { projects, fetchProjects } = useProjectStore();
  const { accounts, fetchSummary } = useFinanceStore();

  const isEdit = Boolean(editing);
  const [saving, setSaving] = useState(false);
  const [files, setFiles] = useState<File[]>([]);
  const [fileCategory, setFileCategory] = useState("Supporting Document");

  const [f, setF] = useState({
    invoiceNumber: editing?.invoiceNumber || "",
    invoiceType: editing?.invoiceType || "Miscellaneous / Custom Invoice",
    invoiceDate: (editing?.invoiceDate || new Date().toISOString()).slice(0, 10),
    dueDate: editing?.dueDate ? editing.dueDate.slice(0, 10) : "",
    currency: editing?.currency || "USD",
    client: (editing?.client?._id as string) || "",
    billingAddress: editing?.billingAddress || "",
    contactEmail: editing?.contactEmail || "",
    project: (editing?.project?._id as string) || "",
    milestoneIndex: editing?.milestoneIndex ?? "",
    upworkAccount: (editing?.upworkAccount?._id as string) || "",
    description: editing?.description || "",
    notes: editing?.notes || "",
    customTypeLabel: editing?.customTypeLabel || "",
    paymentMethod: editing?.paymentMethod || "",
    paymentTerms: editing?.paymentTerms || "",
    amount: editing?.amount || (editing?.lineItems?.length ? editing.lineItems.reduce((s: number, li: any) => s + ((li.quantity || 1) * (li.unitPrice || 0)), 0) : ""),
    recurring: editing?.recurring || false,
    recurringInterval: editing?.recurringInterval || "Monthly",
  });
  // Selected payment methods (checkboxes) — one or more.
  const [methods, setMethods] = useState<string[]>(
    editing?.acceptedMethods?.length ? editing.acceptedMethods : (editing?.paymentGateway ? [editing.paymentGateway] : [])
  );
  // Payment methods configured & active in Settings (dynamic — no credentials here).
  const [activeMethods, setActiveMethods] = useState<{ key: string; label: string }[]>([]);
  const [payEnv, setPayEnv] = useState<string>("");

  useEffect(() => {
    if (!clients.length) fetchClients();
    if (!projects.length) fetchProjects();
    if (!accounts.length) fetchSummary();
    axiosInstance.get("/payment-settings/active-methods")
      .then((r) => {
        const d = r.data || {};
        setActiveMethods(d.methods || []);
        setPayEnv(d.environment || "");
        // New invoices inherit the Settings defaults (currency, due period, terms).
        if (!editing) {
          setF((p) => ({
            ...p,
            currency: d.currency || p.currency,
            paymentTerms: p.paymentTerms || d.paymentTerms || "",
            dueDate: p.dueDate || (d.duePeriodDays
              ? new Date(Date.now() + Number(d.duePeriodDays) * 86400000).toISOString().slice(0, 10)
              : p.dueDate),
          }));
        }
      })
      .catch(() => { /* settings optional — fall back to the static list */ });
  }, []);

  // Invoice type options — Upwork clients are billed on Upwork itself, so no "Upwork Invoice" type here.
  const CUSTOM_TYPE = "Miscellaneous / Custom Invoice";
  const invoiceTypeOptions = (types.length ? types : [f.invoiceType]).filter((t) => t !== "Upwork Invoice");
  const isCustomType = f.invoiceType === CUSTOM_TYPE;

  const selectedProject: any = useMemo(() => projects.find((p: any) => p._id === f.project), [projects, f.project]);
  const milestones = selectedProject?.milestones || [];

  // All configured methods are offered to every contact — an Upwork client who comes
  // off-platform can still pay via Stripe/PayPal, so nothing is hidden.
  const offeredMethods = activeMethods.length
    ? activeMethods
    : PAYMENT_METHODS.filter(Boolean).map((m) => ({ key: m, label: m }));

  const set = (k: string, v: any) => setF((p) => ({ ...p, [k]: v }));
  const toggleMethod = (k: string) => setMethods((m) => m.includes(k) ? m.filter((x) => x !== k) : [...m, k]);

  // Map a gateway key (stripe/paypal/…) to the legacy paymentMethod enum label.
  const paymentMethodLabel = (key: string) => {
    if (!key) return "";
    if ((PAYMENT_METHODS as string[]).includes(key)) return key; // already a valid label
    const map: Record<string, string> = { stripe: "Stripe", paypal: "PayPal", bank_transfer: "Bank Transfer", manual: "Other", cash: "Cash", wise: "Wise", payoneer: "Payoneer" };
    return map[key.toLowerCase()] || "Other";
  };

  const build = (status?: string) => {
    return {
      ...f,
      currency: f.currency || "USD",
      customTypeLabel: isCustomType ? f.customTypeLabel : "",
      amount: Number(f.amount) || 0,
      lineItems: [],
      milestoneIndex: f.milestoneIndex === "" ? null : Number(f.milestoneIndex),
      upworkAccount: f.upworkAccount || null,
      acceptedMethods: methods,
      paymentGateway: methods[0] || "",
      paymentMethod: paymentMethodLabel(methods[0] || f.paymentMethod || ""),
      recurringInterval: f.recurring ? f.recurringInterval : "",
      projectSource: selectedProject?.source || "Direct",
      ...(status ? { status } : {}),
    };
  };

  const uploadFiles = async (id: string) => {
    if (!files.length) return;
    try {
      const fd = new FormData();
      files.forEach((file) => fd.append("files", file));
      fd.append("category", fileCategory);
      await axiosInstance.post(`/invoices/${id}/attachments`, fd, { headers: { "Content-Type": "multipart/form-data" } });
    } catch {
      toast.error("Invoice saved, but attachment upload failed");
    }
  };

  const submit = async (mode: "draft" | "generate" | "send") => {
    if (!f.client) { toast.error("Select a client"); return; }
    setSaving(true);
    const status = mode === "draft" ? "Upcoming" : "Submitted";
    let ok = false;
    let id = editing?._id;
    if (isEdit && editing) {
      ok = await updateInvoice(editing._id, build(status));
    } else {
      const created = await createInvoice(build(status));
      ok = Boolean(created);
      id = created?._id;
    }
    if (ok && id) {
      await uploadFiles(id);
      if (mode === "send") await sendReminder(id, "Client");
    }
    setSaving(false);
    if (ok) { onSaved(); onClose(); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/30 backdrop-blur-sm" onClick={onClose}>
      <aside onClick={(e) => e.stopPropagation()} className="absolute top-0 right-0 h-full w-full max-w-2xl bg-white shadow-2xl flex flex-col">
        <div className="p-5 border-b border-slate-200 flex items-center justify-between bg-gradient-to-br from-blue-50/50 to-white">
          <h2 className="text-lg font-extrabold text-slate-900">{isEdit ? `Edit ${editing?.invoiceNumber}` : "New Invoice"}</h2>
          <button onClick={onClose} className="p-1.5 text-slate-400 hover:bg-slate-100 rounded-full"><X size={18} /></button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-6">
          {/* Invoice Info */}
          <Section title="Invoice Information">
            <div className="grid grid-cols-2 gap-3">
              <Field label="Invoice Number"><input value={f.invoiceNumber || "Auto-generated on save"} readOnly disabled title="System-generated & immutable" className={`${inp} bg-slate-100 text-slate-500 cursor-not-allowed`} /></Field>
              <Field label="Invoice Type"><select value={f.invoiceType} onChange={(e) => set("invoiceType", e.target.value)} className={inp}>{invoiceTypeOptions.map((t) => <option key={t} value={t}>{t}</option>)}</select></Field>
              <Field label="Invoice Date"><input type="date" value={f.invoiceDate} onChange={(e) => set("invoiceDate", e.target.value)} className={inp} /></Field>
              <Field label="Due Date"><input type="date" value={f.dueDate} onChange={(e) => set("dueDate", e.target.value)} className={inp} /></Field>
              <Field label="Currency"><input value="USD" readOnly disabled className={`${inp} bg-slate-100 text-slate-500 cursor-not-allowed`} /></Field>
              {isCustomType && (
                <div className="col-span-2"><Field label="Custom invoice name *"><input value={f.customTypeLabel} onChange={(e) => set("customTypeLabel", e.target.value)} placeholder="e.g. Retainer — August, Consultation fee…" className={inp} /></Field></div>
              )}
            </div>
          </Section>

          {/* Client */}
          <Section title="Contact Information">
            <div className="grid grid-cols-2 gap-3">
              <Field label="Contact *"><select value={f.client} onChange={(e) => set("client", e.target.value)} className={inp}><option value="">Select contact…</option>{clients.map((c) => <option key={c._id} value={c._id}>{c.name}</option>)}</select></Field>
              <Field label="Contact Email"><input value={f.contactEmail} onChange={(e) => set("contactEmail", e.target.value)} placeholder="billing@…" className={inp} /></Field>
              <div className="col-span-2"><Field label="Billing Address"><input value={f.billingAddress} onChange={(e) => set("billingAddress", e.target.value)} className={inp} /></Field></div>
            </div>
          </Section>

          {/* Project */}
          <Section title="Project Information (optional)">
            <div className="grid grid-cols-2 gap-3">
              <Field label="Linked Project"><select value={f.project} onChange={(e) => { set("project", e.target.value); set("milestoneIndex", ""); }} className={inp}><option value="">— None —</option>{projects.map((p: any) => <option key={p._id} value={p._id}>{p.name}</option>)}</select></Field>
              <Field label="Linked Milestone"><select value={f.milestoneIndex} onChange={(e) => set("milestoneIndex", e.target.value)} className={inp} disabled={!milestones.length}><option value="">— None —</option>{milestones.map((m: any, i: number) => <option key={i} value={i}>{m.title || `Milestone ${i + 1}`} (${m.amount})</option>)}</select></Field>
              <Field label="Upwork Account (if applicable)"><select value={f.upworkAccount} onChange={(e) => set("upworkAccount", e.target.value)} className={inp}><option value="">— None —</option>{accounts.map((a: any) => <option key={a.accountId} value={a.accountId}>{a.accountName}</option>)}</select></Field>
            </div>
          </Section>

          {/* Invoice details — kept simple: what it's for + how much */}
          <Section title="Invoice Details">
            <Field label="What is this invoice for?"><input value={f.description} onChange={(e) => set("description", e.target.value)} placeholder="e.g. Website design — Milestone 2" className={inp} /></Field>
            <div className="mt-3">
              <Field label="Amount (USD) *">
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 font-semibold">$</span>
                  <input type="number" min={0} value={f.amount} onChange={(e) => set("amount", e.target.value)} placeholder="0.00" className={`${inp} pl-7 text-right font-semibold`} />
                </div>
              </Field>
            </div>
            <div className="mt-3"><Field label="Notes (optional)"><textarea value={f.notes} onChange={(e) => set("notes", e.target.value)} rows={2} placeholder="Anything the client should know…" className={inp} /></Field></div>
          </Section>

          {/* Payment */}
          <Section title="Payment Information">
            {payEnv && (
              <p className="text-[11px] font-semibold mb-2 inline-flex items-center gap-1.5">
                <span className={`w-2 h-2 rounded-full ${payEnv === "live" ? "bg-emerald-500" : "bg-amber-400"}`} />
                <span className={payEnv === "live" ? "text-emerald-700" : "text-amber-700"}>{payEnv === "live" ? "LIVE" : "SANDBOX"} payment environment</span>
              </p>
            )}
            <Field label="How can the client pay? (select one or more)">
              <div className="flex flex-wrap gap-2 mt-1">
                {offeredMethods.map((m) => {
                  const on = methods.includes(m.key);
                  return (
                    <button
                      type="button"
                      key={m.key}
                      onClick={() => toggleMethod(m.key)}
                      className={`px-3 py-2 text-[13px] font-semibold rounded-lg border transition ${on ? "bg-blue-50 border-blue-500 text-blue-700" : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50"}`}
                    >
                      {on ? "✓ " : ""}{m.label}
                    </button>
                  );
                })}
              </div>
              {offeredMethods.length === 0 && (
                <span className="text-[10px] text-amber-600">No payment methods available for this contact.</span>
              )}
            </Field>

            <div className="grid grid-cols-2 gap-3 mt-3">
              <Field label="Billing">
                <div className="flex gap-2">
                  <button type="button" onClick={() => set("recurring", false)} className={`flex-1 px-3 py-2 text-[13px] font-semibold rounded-lg border ${!f.recurring ? "bg-blue-50 border-blue-500 text-blue-700" : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50"}`}>One-time</button>
                  <button type="button" onClick={() => set("recurring", true)} className={`flex-1 px-3 py-2 text-[13px] font-semibold rounded-lg border ${f.recurring ? "bg-blue-50 border-blue-500 text-blue-700" : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50"}`}>Recurring</button>
                </div>
              </Field>
              {f.recurring ? (
                <Field label="Repeat every">
                  <select value={f.recurringInterval} onChange={(e) => set("recurringInterval", e.target.value)} className={inp}>
                    <option value="Weekly">Weekly</option>
                    <option value="Monthly">Monthly</option>
                    <option value="Quarterly">Quarterly</option>
                    <option value="Yearly">Yearly</option>
                  </select>
                </Field>
              ) : (
                <Field label="Payment Terms"><input value={f.paymentTerms} onChange={(e) => set("paymentTerms", e.target.value)} placeholder="e.g. Net 15" className={inp} /></Field>
              )}
            </div>
          </Section>

          {/* Attachments */}
          <Section title="Attachments">
            <div className="flex flex-wrap items-center gap-2">
              <select value={fileCategory} onChange={(e) => setFileCategory(e.target.value)} className={inpsm}>
                <option>Supporting Document</option><option>Purchase Order</option><option>Agreement</option><option>Other</option>
              </select>
              <label className="px-3 py-1.5 bg-slate-50 border border-slate-200 rounded-lg text-xs font-semibold cursor-pointer hover:bg-slate-100">
                Choose files
                <input type="file" multiple accept=".jpg,.jpeg,.png,.pdf" className="hidden" onChange={(e) => setFiles((p) => [...p, ...Array.from(e.target.files || [])])} />
              </label>
              <span className="text-[11px] text-slate-400">JPG, PNG, PDF</span>
            </div>
            {files.length > 0 && (
              <div className="mt-2 flex flex-col gap-1">
                {files.map((file, i) => (
                  <div key={i} className="flex items-center justify-between bg-slate-50 rounded px-2.5 py-1.5 text-[12px]">
                    <span className="truncate">{file.name}</span>
                    <button onClick={() => setFiles(files.filter((_, j) => j !== i))} className="text-slate-400 hover:text-rose-600"><X size={13} /></button>
                  </div>
                ))}
              </div>
            )}
          </Section>
        </div>

        <div className="p-4 border-t border-slate-200 bg-slate-50/50 flex flex-wrap justify-end gap-2">
          <button onClick={() => submit("draft")} disabled={saving} className="px-3 py-2 text-[13px] font-semibold border border-slate-200 rounded-lg text-slate-700 hover:bg-white inline-flex items-center gap-1.5 disabled:opacity-50"><Save size={14} /> Save Draft</button>
          <button onClick={() => submit("generate")} disabled={saving} className="px-3 py-2 text-[13px] font-semibold bg-slate-800 text-white rounded-lg hover:bg-slate-900 inline-flex items-center gap-1.5 disabled:opacity-50"><FileCheck2 size={14} /> Generate Invoice</button>
          <button onClick={() => submit("send")} disabled={saving} className="px-3 py-2 text-[13px] font-semibold bg-[#0058c3] text-white rounded-lg hover:bg-[#004b9e] inline-flex items-center gap-1.5 disabled:opacity-50"><Send size={14} /> Generate &amp; Send</button>
        </div>
      </aside>
    </div>
  );
}

const inp = "w-full px-3 py-2 text-[13px] bg-slate-50 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500";
const inpsm = "px-2 py-1.5 text-[12px] bg-white border border-slate-200 rounded focus:outline-none focus:ring-1 focus:ring-blue-500";

const Section = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <div>
    <h3 className="text-[11px] font-bold uppercase tracking-[0.12em] text-slate-500 mb-2.5">{title}</h3>
    {children}
  </div>
);
const Field = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div>
    <label className="block text-[11px] font-semibold text-slate-600 mb-1">{label}</label>
    {children}
  </div>
);
